import {
    chartRefreshScheduler,
    computeStaleState,
    formatAge,
    FrameBudgetTracker,
    measureFrame,
    RingBuffer,
    getStaleThresholdMs,
    setSnapshot,
    getSnapshot,
    reportChartRenderMetric,
} from '../../charts/engine';
import { renderDonutChart, renderHeatmapGrid, renderLineChart } from '../../charts/primitives';
import { fetchPriorityHeadlines, type NewsItem } from '../../services/news-feed';
import { isFeatureEnabled } from '../../services/runtime-config';

type IntelligenceSnapshot = {
    headlines: NewsItem[];
    velocitySeries: number[];
    shockSeries: number[];
    negativeSentimentSeries: number[];
    sourceConcentrationSeries: number[];
    lastUpdatedAt: number;
};

const teardownMap = new WeakMap<HTMLElement, () => void>();

const IMPACT_KEYWORDS: Record<string, string[]> = {
  policy: ['rbi', 'fed', 'ecb', 'policy', 'rate', 'inflation', 'cpi', 'fomc', 'repo'],
  geopolitics: ['war', 'attack', 'missile', 'sanction', 'conflict', 'ceasefire', 'border', 'tariff'],
  macro: ['gdp', 'recession', 'deficit', 'growth', 'employment', 'pmis', 'factory', 'output'],
  earnings: ['results', 'earnings', 'profit', 'guidance', 'revenue', 'margin', 'quarter'],
  liquidity: ['bond', 'yield', 'liquidity', 'credit', 'spread', 'funding', 'default', 'downgrade'],
  commodities: ['oil', 'crude', 'gold', 'gas', 'copper', 'commodity'],
};

function sentimentToScore(sentiment: string | undefined): number {
    const normalized = (sentiment || '').toLowerCase();
    if (normalized.includes('positive') || normalized.includes('bull')) return 2;
    if (normalized.includes('negative') || normalized.includes('bear')) return -2;
    if (normalized.includes('neutral')) return 0;
    return 0;
}

function parseAgeMinutes(timeAgo: string): number {
    const text = (timeAgo || '').toLowerCase().trim();
    if (!text) return 180;
    if (text.includes('just now')) return 0;

    const match = text.match(/(\d+)/);
    if (!match) return 180;
    const value = Number(match[1]);
    if (Number.isNaN(value)) return 180;

    if (text.includes('m')) return value;
    if (text.includes('h')) return value * 60;
    if (text.includes('d')) return value * 60 * 24;
    return 180;
}

function computeHeadlineVelocity(headlines: NewsItem[]): number {
    if (headlines.length === 0) return 0;

    const now = Date.now();
    const recencyWeighted = headlines.reduce((sum, item) => {
        const ageMinutes = item.pubDate
            ? Math.max(0, Math.floor((now - new Date(item.pubDate).getTime()) / 60000))
            : parseAgeMinutes(item.timeAgo);
        const score = Math.max(0, 120 - Math.min(120, ageMinutes));
        return sum + score;
    }, 0);

    return Number((recencyWeighted / headlines.length).toFixed(2));
}

function toImpactTheme(item: NewsItem): string {
  const text = `${item.title} ${item.description} ${item.category}`.toLowerCase();
  let bestTheme = 'general';
  let bestScore = 0;
  Object.entries(IMPACT_KEYWORDS).forEach(([theme, tokens]) => {
    const score = tokens.reduce((sum, token) => sum + (text.includes(token) ? 1 : 0), 0);
    if (score > bestScore) {
      bestTheme = theme;
      bestScore = score;
    }
  });
  return bestTheme;
}

function impactScore(item: NewsItem): number {
  const sentiment = sentimentToScore(item.sentiment);
  const text = `${item.title} ${item.description}`.toLowerCase();
  const shockHits = (text.match(/(surge|plunge|crash|spike|ban|sanction|default|attack|liquidity|record|emergency)/g) || []).length;
  const ageMin = item.pubDate
    ? Math.max(0, Math.floor((Date.now() - new Date(item.pubDate).getTime()) / 60000))
    : parseAgeMinutes(item.timeAgo);
  const recencyFactor = Math.max(0.3, 1 - (Math.min(240, ageMin) / 300));
  return ((Math.abs(sentiment) + (shockHits * 0.85)) * recencyFactor) * (sentiment < 0 ? 1.15 : 1);
}

function computeNarrativeShock(headlines: NewsItem[]): number {
  if (headlines.length === 0) return 0;
  const impact = headlines.reduce((sum, item) => sum + impactScore(item), 0) / headlines.length;
  return Number(Math.min(100, impact * 18).toFixed(2));
}

function toImpactPressureCells(headlines: NewsItem[]): Array<{ x: number; y: number; value: number }> {
  return headlines.slice(0, 12).map((item, idx) => {
    const sentiment = sentimentToScore(item.sentiment);
    const score = impactScore(item);
    const signed = sentiment < 0 ? -score : score;
    return {
      x: idx % 6,
      y: Math.floor(idx / 6),
      value: Math.max(-5, Math.min(5, signed)),
    };
  });
}

function buildImpactVectors(headlines: NewsItem[]): Array<{ theme: string; intensity: number; count: number }> {
  const vectors = new Map<string, { intensity: number; count: number }>();
  headlines.slice(0, 20).forEach((item) => {
    const theme = toImpactTheme(item);
    const rec = vectors.get(theme) || { intensity: 0, count: 0 };
    rec.intensity += impactScore(item);
    rec.count += 1;
    vectors.set(theme, rec);
  });

  return Array.from(vectors.entries())
    .map(([theme, rec]) => ({
      theme,
      intensity: rec.count > 0 ? rec.intensity / rec.count : 0,
      count: rec.count,
    }))
    .sort((a, b) => b.intensity - a.intensity)
    .slice(0, 6);
}

export function renderPhase2IntelligenceEngine(container: HTMLElement): void {
    if (!isFeatureEnabled('charts_v2_engine')) {
        container.innerHTML = '<div class="wm-empty">charts_v2_engine flag is disabled.</div>';
        return;
    }

    teardownMap.get(container)?.();

    container.innerHTML = `
      <div class="v2-chart-card glass-card">
        <div class="card-header">
          <h3>Phase 2 Engine: Intelligence Pulse</h3>
          <span class="text-xs text-muted">Tier B refresh</span>
        </div>

        <div class="v2-chart-grid">
          <div class="v2-chart-slot">
            <div class="v2-chart-title">Headline Velocity</div>
            <canvas id="v2-intel-velocity" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Source Distribution</div>
            <canvas id="v2-intel-source-donut" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Sentiment Heat Grid</div>
            <canvas id="v2-intel-sentiment" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Category Mix</div>
            <canvas id="v2-intel-category-donut" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Narrative Shock Index</div>
            <canvas id="v2-intel-shock" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Impact Pressure Grid</div>
            <canvas id="v2-intel-impact-grid" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Negative Sentiment Pulse</div>
            <canvas id="v2-intel-negative-sentiment" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Source Concentration Pulse</div>
            <canvas id="v2-intel-source-concentration" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot v2-chart-slot--wide">
            <div class="v2-chart-title">Priority Headlines</div>
            <div id="v2-intel-headlines" class="v2-sector-board"></div>
          </div>

          <div class="v2-chart-slot v2-chart-slot--wide">
            <div class="v2-chart-title">Top Impact Vectors</div>
            <div id="v2-intel-vectors" class="v2-sector-board"></div>
          </div>
        </div>

        <div class="v2-chart-meta">
          <span id="v2-intel-freshness" class="text-xs">Waiting for live intelligence feed...</span>
          <span id="v2-intel-frame" class="text-xs text-muted" style="margin-left: var(--space-3);">Frame: --</span>
        </div>

        <div id="v2-intel-stale" class="v2-stale-overlay" style="display:none;">
          Intelligence feed delayed. Rendering cached snapshot.
        </div>
      </div>
    `;

    const velocityCanvas = container.querySelector('#v2-intel-velocity') as HTMLCanvasElement | null;
    const sourceDonut = container.querySelector('#v2-intel-source-donut') as HTMLCanvasElement | null;
    const sentimentCanvas = container.querySelector('#v2-intel-sentiment') as HTMLCanvasElement | null;
    const categoryDonut = container.querySelector('#v2-intel-category-donut') as HTMLCanvasElement | null;
    const shockCanvas = container.querySelector('#v2-intel-shock') as HTMLCanvasElement | null;
    const impactGridCanvas = container.querySelector('#v2-intel-impact-grid') as HTMLCanvasElement | null;
    const negativeSentimentCanvas = container.querySelector('#v2-intel-negative-sentiment') as HTMLCanvasElement | null;
    const sourceConcentrationCanvas = container.querySelector('#v2-intel-source-concentration') as HTMLCanvasElement | null;
    const headlinesNode = container.querySelector('#v2-intel-headlines') as HTMLElement | null;
    const vectorsNode = container.querySelector('#v2-intel-vectors') as HTMLElement | null;
    const freshnessNode = container.querySelector('#v2-intel-freshness') as HTMLElement | null;
    const frameNode = container.querySelector('#v2-intel-frame') as HTMLElement | null;
    const staleNode = container.querySelector('#v2-intel-stale') as HTMLElement | null;

    if (!velocityCanvas || !sourceDonut || !sentimentCanvas || !categoryDonut || !shockCanvas || !impactGridCanvas || !negativeSentimentCanvas || !sourceConcentrationCanvas || !headlinesNode || !vectorsNode || !freshnessNode || !frameNode || !staleNode) return;

    const snapshotKey = 'phase2:intelligence-engine';
    const staleThresholdMs = getStaleThresholdMs('B');
    const frameTracker = new FrameBudgetTracker(180, 16);
    const velocitySeries = new RingBuffer<number>(180);
    const shockSeries = new RingBuffer<number>(180);
    const negativeSentimentSeries = new RingBuffer<number>(180);
    const sourceConcentrationSeries = new RingBuffer<number>(180);

    let lastUpdatedAt: number | null = null;
    let lastHeadlines: NewsItem[] = [];

    const draw = (headlines: NewsItem[]): void => {
        const sample = measureFrame('intelligence-engine', () => {
            renderLineChart(velocityCanvas, velocitySeries.toArray(), '#5aa6ff');

            const sourceCounts = new Map<string, number>();
            const categoryCounts = new Map<string, number>();
            headlines.forEach((item) => {
                sourceCounts.set(item.source || 'Unknown', (sourceCounts.get(item.source || 'Unknown') || 0) + 1);
                categoryCounts.set(item.category || 'general', (categoryCounts.get(item.category || 'general') || 0) + 1);
            });

            const sourceValues = Array.from(sourceCounts.values());
            const categoryValues = Array.from(categoryCounts.values());
            renderDonutChart(sourceDonut, sourceValues.length ? sourceValues : [1], ['#5aa6ff', '#15b57a', '#ff9f43', '#d94736', '#9370db']);
            renderDonutChart(categoryDonut, categoryValues.length ? categoryValues : [1], ['#15b57a', '#5aa6ff', '#ff9f43', '#d94736', '#7cc4fa']);

            const cells = headlines.slice(0, 12).map((item, idx) => ({
                x: idx % 6,
                y: Math.floor(idx / 6),
                value: sentimentToScore(item.sentiment),
            }));
            renderHeatmapGrid(sentimentCanvas, cells, 6, 2);
            renderLineChart(shockCanvas, shockSeries.toArray(), '#d94736');
            renderHeatmapGrid(impactGridCanvas, toImpactPressureCells(headlines), 6, 2);
            renderLineChart(negativeSentimentCanvas, negativeSentimentSeries.toArray(), '#ff9f43');
            renderLineChart(sourceConcentrationCanvas, sourceConcentrationSeries.toArray(), '#5aa6ff');

            const top = headlines.slice(0, 10);
            headlinesNode.innerHTML = top.length
                ? top.map((item) => {
                    const sentimentScore = sentimentToScore(item.sentiment);
                    const tone = sentimentScore > 0 ? 'terminal-text--up' : sentimentScore < 0 ? 'terminal-text--down' : '';
                    return `<div class="terminal-list__row"><span>${item.title}</span><strong class="${tone}">${item.source} · ${item.timeAgo}</strong></div>`;
                }).join('')
                : '<div class="wm-empty">No live headlines available.</div>';

              const vectors = buildImpactVectors(headlines);
              vectorsNode.innerHTML = vectors.length > 0
                ? vectors.map((entry) => {
                  const tone = entry.intensity >= 2.4 ? 'terminal-text--down' : entry.intensity >= 1.2 ? '' : 'terminal-text--up';
                  return `<div class="terminal-list__row"><span>${entry.theme.toUpperCase()} <span class="text-xs text-muted">${entry.count} hits</span></span><strong class="${tone}">${entry.intensity.toFixed(2)}</strong></div>`;
                }).join('')
                : '<div class="wm-empty">No active impact vectors.</div>';
        });

        frameTracker.push(sample.label, sample.durationMs);
        const avg = frameTracker.getAverageMs();
        const within = frameTracker.isWithinBudget(sample.durationMs);
        frameNode.textContent = `Frame: ${sample.durationMs.toFixed(2)}ms | avg ${avg.toFixed(2)}ms | budget ${within ? 'OK' : 'OVER'}`;

        reportChartRenderMetric({
            id: 'phase2-intelligence',
            label: 'Intelligence Pulse',
            latestMs: sample.durationMs,
            averageMs: avg,
            budgetMs: frameTracker.getBudgetMs(),
            withinBudget: within,
        });

        const stale = computeStaleState(lastUpdatedAt, staleThresholdMs);
        staleNode.style.display = stale.stale ? 'flex' : 'none';
        freshnessNode.textContent = stale.stale
            ? `Stale: ${formatAge(stale.ageMs)} old`
            : `Live update ${new Date(lastUpdatedAt || Date.now()).toLocaleTimeString('en-IN')}`;
    };

    const persist = (): void => {
        setSnapshot<IntelligenceSnapshot>(snapshotKey, {
            headlines: lastHeadlines,
            velocitySeries: velocitySeries.toArray(),
            shockSeries: shockSeries.toArray(),
            negativeSentimentSeries: negativeSentimentSeries.toArray(),
            sourceConcentrationSeries: sourceConcentrationSeries.toArray(),
            lastUpdatedAt: lastUpdatedAt || Date.now(),
        });
    };

    const update = async () => {
        try {
            const headlines = await fetchPriorityHeadlines();
            if (!headlines.length) throw new Error('no intelligence headlines');

            const velocity = computeHeadlineVelocity(headlines);
            const shock = computeNarrativeShock(headlines);
            const negativeCount = headlines.filter((item) => sentimentToScore(item.sentiment) < 0).length;
            const negativePct = headlines.length > 0 ? (negativeCount / headlines.length) * 100 : 0;

            const sourceCounts = new Map<string, number>();
            headlines.forEach((item) => {
              const source = item.source || 'Unknown';
              sourceCounts.set(source, (sourceCounts.get(source) || 0) + 1);
            });
            const maxSourceCount = Array.from(sourceCounts.values()).reduce((max, value) => Math.max(max, value), 0);
            const sourceConcentration = headlines.length > 0 ? (maxSourceCount / headlines.length) * 100 : 0;

            velocitySeries.push(velocity);
            shockSeries.push(shock);
            negativeSentimentSeries.push(negativePct);
            sourceConcentrationSeries.push(sourceConcentration);
            lastHeadlines = headlines;
            lastUpdatedAt = Date.now();
            persist();
            draw(headlines);
        } catch {
            if (lastHeadlines.length > 0) draw(lastHeadlines);
        }
    };

    const persisted = getSnapshot<IntelligenceSnapshot>(snapshotKey);
    if (persisted?.headlines?.length) {
        lastHeadlines = persisted.headlines;
        persisted.velocitySeries.forEach((value) => velocitySeries.push(value));
        persisted.shockSeries?.forEach((value) => shockSeries.push(value));
        persisted.negativeSentimentSeries?.forEach((value) => negativeSentimentSeries.push(value));
        persisted.sourceConcentrationSeries?.forEach((value) => sourceConcentrationSeries.push(value));
        lastUpdatedAt = persisted.lastUpdatedAt;
        draw(lastHeadlines);
    }

    const taskId = `phase2-intelligence-${Math.random().toString(36).slice(2)}`;
    const unregister = chartRefreshScheduler.register(taskId, 'B', update, 5000);

    teardownMap.set(container, () => {
        unregister();
    });
}
